import { LANDMASSES } from "@tiny-seas/shared";
import { DAY_SKY_HORIZON, DAY_SKY_ZENITH, DAY_SUN_DIRECTION } from "./atmosphere.ts";
import type { BakeJob, BakeOutput } from "./bake-worker.ts";
import { SURFACE_PROFILES, type SurfaceFamily } from "./materials.ts";
import { SHORE, SHORE_SIZE, bakeShoreAtlas, placeShoreRows } from "./shore-bake.ts";
import { buildSkullFort, type SkullFortPart } from "./skull-fort.ts";
import { ENVIRONMENT_SIZE, bakeSkyRadiance, type SkyInputs, type SkyRadiance } from "./sky-bake.ts";

/**
 * World bakes that depend only on static data: the shore atlas, the day sky's IBL
 * radiance and Skull's fort geometry (≈19 s of single-threaded CPU on a fast laptop).
 * `prepareWorldBakes` slices them across a few workers while the lobby is up;
 * `Environment` then reads the results synchronously. Workers run the same
 * functions, so the data matches a main-thread bake exactly.
 */

/** Leaves a core for the page; more workers than this gain little. */
const MAX_WORKERS = 8;
/** Rows per job: small enough to balance the costly islands across workers. */
const SHORE_ROWS = 64;
const SKY_ROWS = 32;

let shore: Uint8Array | null = null;
const skies: { inputs: SkyInputs; radiance: SkyRadiance }[] = [];
let fort: SkullFortPart[] | null = null;
let preparing: Promise<void> | null = null;

/** The sky `Environment` lights the world with (`setupDaylightIBL` derives the same numbers). */
function daySky(): SkyInputs {
  const sun = DAY_SUN_DIRECTION.scale(-1).normalize();
  return { zenith: [DAY_SKY_ZENITH.r, DAY_SKY_ZENITH.g, DAY_SKY_ZENITH.b], horizon: [DAY_SKY_HORIZON.r, DAY_SKY_HORIZON.g, DAY_SKY_HORIZON.b], sun: [sun.x, sun.y, sun.z] };
}

/** Each surface family's texture repeat, which fort UVs divide by (a plain object, so it posts to a worker). */
function surfaceMetres(): Record<SurfaceFamily, number> {
  return Object.fromEntries(Object.entries(SURFACE_PROFILES).map(([family, profile]) => [family, profile.metres])) as Record<SurfaceFamily, number>;
}

/** Bakes the shore atlas, day sky and Skull fort across workers, once per page (a failed bake may be retried). */
export function prepareWorldBakes(): Promise<void> {
  preparing ??= (async () => {
    if (shore && skies.length && fort) return;
    const pool = new BakePool(Math.max(1, Math.min(MAX_WORKERS, (navigator.hardwareConcurrency || 4) - 1)));
    try {
      const sky = daySky();
      // Longest chains queue first: the fort is one job, the sky two dependent passes.
      const [parts, radiance, atlas] = await Promise.all([pool.run({ kind: "skullFort", metres: surfaceMetres() }), bakeSky(pool, sky), bakeShore(pool)]);
      fort = parts;
      shore = atlas;
      skies.push({ inputs: sky, radiance });
    } finally {
      pool.terminate();
    }
  })().catch((e: unknown) => {
    preparing = null;
    throw e;
  });
  return preparing;
}

/** The shore atlas (`shore-bake.ts`), baked on this thread if `prepareWorldBakes` has not. */
export function shoreAtlas(): Uint8Array {
  shore ??= bakeShoreAtlas();
  return shore;
}

/** Linear radiance of `sky` (`sky-bake.ts`), baked on this thread unless already prepared. */
export function daylightSky(sky: SkyInputs): SkyRadiance {
  const same = (a: readonly number[], b: readonly number[]): boolean => a.every((v, i) => v === b[i]);
  let entry = skies.find(({ inputs }) => same(inputs.zenith, sky.zenith) && same(inputs.horizon, sky.horizon) && same(inputs.sun, sky.sun));
  if (!entry) skies.push(entry = { inputs: sky, radiance: bakeSkyRadiance(sky) });
  return entry.radiance;
}

/** Skull's fort geometry (`skull-fort.ts`), built on this thread if `prepareWorldBakes` has not. Read-only. */
export function skullFort(): readonly SkullFortPart[] {
  fort ??= buildSkullFort(surfaceMetres());
  return fort;
}

async function bakeShore(pool: BakePool): Promise<Uint8Array> {
  const atlas = new Uint8Array(SHORE_SIZE * SHORE_SIZE * 4);
  const jobs: Promise<void>[] = [];
  for (let index = 0; index < LANDMASSES.length; index++) {
    for (let y0 = 0; y0 < SHORE.tile; y0 += SHORE_ROWS) {
      const y1 = Math.min(SHORE.tile, y0 + SHORE_ROWS);
      jobs.push(pool.run({ kind: "shore", index, y0, y1 }).then((rows) => placeShoreRows(atlas, index, y0, rows)));
    }
  }
  await Promise.all(jobs);
  return atlas;
}

async function bakeSky(pool: BakePool, sky: SkyInputs): Promise<SkyRadiance> {
  const size = ENVIRONMENT_SIZE;
  const base = await Promise.all(Array.from({ length: 6 }, async (_, face) => {
    const out = new Float64Array(size * size * 3);
    const bands: Promise<void>[] = [];
    for (let y0 = 0; y0 < size; y0 += SKY_ROWS) {
      const y1 = Math.min(size, y0 + SKY_ROWS);
      bands.push(pool.run({ kind: "skyBase", sky, face, y0, y1 }).then((rows) => out.set(rows, y0 * size * 3)));
    }
    await Promise.all(bands);
    return out;
  }));
  // Filtering samples the base level at float precision, as the single-threaded bake does.
  const linear = base.map((face) => Float32Array.from(face));
  const filtered = await Promise.all(linear.map((_, face) => pool.run({ kind: "skyFiltered", face, base: linear }, true)));
  return [base, ...filtered[0]!.map((_, level) => filtered.map((face) => face[level]!))];
}

interface Pending {
  readonly job: BakeJob;
  readonly resolve: (data: unknown) => void;
  readonly reject: (error: unknown) => void;
}

/** Fixed set of bake workers fed from one queue; any worker failure fails every job. */
class BakePool {
  private readonly workers: Worker[] = [];
  private readonly idle: Worker[] = [];
  private readonly queue: Pending[] = [];
  private readonly busy = new Map<Worker, Pending>();
  private failure: Error | null = null;

  constructor(count: number) {
    for (let i = 0; i < count; i++) {
      const worker = new Worker(new URL("./bake-worker.ts", import.meta.url), { type: "module", name: "world-bake" });
      worker.addEventListener("message", (event: MessageEvent<unknown>) => {
        const pending = this.busy.get(worker);
        this.busy.delete(worker);
        pending?.resolve(event.data);
        this.dispatch(worker);
      });
      worker.addEventListener("error", (event) => this.fail(new Error(`world bake worker failed: ${event.message}`)));
      worker.addEventListener("messageerror", () => this.fail(new Error("world bake worker sent an unreadable message")));
      this.workers.push(worker);
      this.idle.push(worker);
    }
  }

  /** Queues a job; `urgent` jobs go ahead of queued ones (the sky's two passes are a chain). */
  run<K extends BakeJob["kind"]>(job: Extract<BakeJob, { kind: K }>, urgent = false): Promise<BakeOutput[K]> {
    if (this.failure) return Promise.reject(this.failure);
    return new Promise<BakeOutput[K]>((resolve, reject) => {
      // The worker answers each job kind with BakeOutput[kind] (bake-worker.ts).
      const pending: Pending = { job, resolve: (data) => resolve(data as BakeOutput[K]), reject };
      if (urgent) this.queue.unshift(pending);
      else this.queue.push(pending);
      const worker = this.idle.pop();
      if (worker) this.dispatch(worker);
    });
  }

  terminate(): void {
    for (const worker of this.workers) worker.terminate();
  }

  private dispatch(worker: Worker): void {
    const pending = this.queue.shift();
    if (!pending || this.failure) {
      this.idle.push(worker);
      return;
    }
    this.busy.set(worker, pending);
    worker.postMessage(pending.job);
  }

  private fail(error: Error): void {
    if (this.failure) return;
    this.failure = error;
    for (const pending of [...this.busy.values(), ...this.queue]) pending.reject(error);
    this.busy.clear();
    this.queue.length = 0;
  }
}
